import type {
  NexusNotification,
  NexusTag,
  NexusTaggers,
  NexusUserCounts,
  NexusUserDetails,
  TUserId,
} from '@/services/nexus/nexus.types';
import { queryNexus } from '@/services/nexus/nexus.utils';
import { userApi } from '@/services/nexus/user/user.api';
import {
  PROFILE_LOOKUP_NOT_FOUND_RETRIES,
  type TUserDetailsParams,
  type TUserPaginationParams,
  type TUserTaggersParams,
  type TUserTagsParams,
} from '@/services/nexus/user/user.types';

/**
 * Nexus User Service
 *
 * Handles fetching user data from Nexus API.
 */
export class NexusUserService {
  /**
   * Retrieves user data from Nexus API
   *
   * @param pubky - User's public key
   * @returns user data (users, posts, streams)
   */
  static async notifications(params: TUserPaginationParams): Promise<NexusNotification[]> {
    const url = userApi.notifications(params);
    return await queryNexus<NexusNotification[]>({ url });
  }

  /**
   * Retrieves tags for a user from Nexus API
   *
   * @param params - Parameters containing user ID and pagination options
   * @returns Array of tags assigned to the user
   */
  static async tags({ force = false, ...params }: TUserTagsParams & { force?: boolean }): Promise<NexusTag[]> {
    const url = userApi.tags(params);
    return await queryNexus<NexusTag[]>({ url, ...(force ? { force: true } : {}) });
  }

  /**
   * Retrieves taggers for a specific tag label on a user from Nexus API
   *
   * @param params - Parameters containing user ID, label, and pagination options
   * @returns Users who tagged the user with the specified label (`{ users, relationship }`)
   */
  static async taggers(params: TUserTaggersParams): Promise<NexusTaggers> {
    const url = userApi.taggers(params);
    // Tagger lists revalidate after local mutations or count changes.
    return await queryNexus<NexusTaggers>({ url, staleTime: 0 });
  }

  /**
   * Retrieves user details from Nexus API.
   *
   * Shared detail consumers retain the indexing retry window. Only a profile page
   * opts into the shorter not-found budget via `profileLookup`.
   *
   * @param params - Parameters containing user ID
   * @returns User details including name, bio, status, image, and links
   */
  static async details({ profileLookup, ...params }: TUserDetailsParams): Promise<NexusUserDetails> {
    const url = userApi.details(params);
    return await queryNexus<NexusUserDetails>({
      url,
      ...(profileLookup ? { notFoundRetries: PROFILE_LOOKUP_NOT_FOUND_RETRIES } : {}),
    });
  }

  /**
   * Retrieves user counts from Nexus API
   *
   * @param params - Parameters containing user ID
   * @returns User counts including followers, following, and friends
   */
  static async counts(params: TUserId): Promise<NexusUserCounts> {
    const url = userApi.counts(params);
    return await queryNexus<NexusUserCounts>({ url });
  }
}
